import { encodeWav } from './encodeWav';

/**
 * AudioWorklet processor code as a string for inline registration.
 * This runs in the audio rendering thread.
 */
const workletCode = `
class RecorderWorkletProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.chunks = [];
    this.isRecording = true;
    this.port.onmessage = (event) => {
      if (event.data.command === 'stop') {
        this.isRecording = false;
        this.port.postMessage({ chunks: this.chunks });
      }
    };
  }

  process(inputs) {
    if (!this.isRecording) {
      return false;
    }
    const input = inputs[0];
    if (input && input[0]) {
      this.chunks.push(new Float32Array(input[0]));
    }
    return true;
  }
}

registerProcessor('recorder-worklet-processor', RecorderWorkletProcessor);
`;

let workletRegistered = false;
let workletBlobUrl: string | null = null;

/**
 * Opens the microphone, records for the given duration, then closes the mic.
 * Returns a WAV-encoded Blob of the recording.
 *
 * Uses AudioWorkletNode for direct PCM access (MediaRecorder does not
 * support WAV output natively in browsers).
 */
export async function recordMicrophoneAsync(durationMs: number): Promise<Blob> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const audioContext = new AudioContext();

  // Register the worklet processor (once per session)
  if (!workletRegistered) {
    workletBlobUrl = URL.createObjectURL(
      new Blob([workletCode], { type: 'application/javascript' })
    );
    await audioContext.audioWorklet.addModule(workletBlobUrl);
    workletRegistered = true;
  } else if (workletBlobUrl) {
    // Re-register for new AudioContext instances
    await audioContext.audioWorklet.addModule(workletBlobUrl);
  }

  const source = audioContext.createMediaStreamSource(stream);
  const workletNode = new AudioWorkletNode(audioContext, 'recorder-worklet-processor');

  source.connect(workletNode);
  workletNode.connect(audioContext.destination);

  return new Promise<Blob>((resolve) => {
    workletNode.port.onmessage = (event: MessageEvent) => {
      if (event.data.chunks) {
        const chunks: Float32Array[] = event.data.chunks;
        const sampleRate = audioContext.sampleRate;

        // Cleanup
        workletNode.disconnect();
        source.disconnect();
        stream.getTracks().forEach((track) => track.stop());
        audioContext.close();

        // Merge chunks into one contiguous buffer
        const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
        const merged = new Float32Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          merged.set(chunk, offset);
          offset += chunk.length;
        }

        resolve(encodeWav(merged, sampleRate));
      }
    };

    // Stop recording after the specified duration
    setTimeout(() => {
      workletNode.port.postMessage({ command: 'stop' });
    }, durationMs);
  });
}
